import React from "react";
import Button from "../../../shared/components/ui/Button";
import FormLabel from "../../../shared/components/ui/FormLabel";
import InputError from "../../../shared/components/ui/InputError";
import Select from "../../../shared/components/ui/Select";
import { Box } from "../../../shared/components/ui/chakra";
import AddIcon from "../../../shared/components/icons/AddIcon";
import { Options } from "../../../shared/lib/types";
import MultiSelect from "./MultiSelect";
import CloseIcon from "../../../shared/components/icons/CloseIcon";
import { FieldError } from "react-hook-form";

import { Flex, Stack } from "@chakra-ui/react";

type MultiStepSelectFormProps = {
  value: { building_type_id: string; structure_type_ids: string[] }[];
  buildingTypes: Options;
  structureTypes: Options;
  labelPrefix: string;
  isDisabled?: boolean;
  errors:
    | { building_type_id?: FieldError; structure_type_ids?: FieldError }[]
    | undefined;
  onChange: (
    value: { building_type_id: string; structure_type_ids: string[] }[],
  ) => void;
};

const BuildingTypeStructureTypeFieldForEdit = ({
  value = [{ building_type_id: "", structure_type_ids: [] }],
  buildingTypes,
  structureTypes,
  labelPrefix,
  errors,
  isDisabled = false,
  onChange,
}: MultiStepSelectFormProps) => {
  const [uniqueIds, setUniqueIds] = React.useState<string[]>(
    value.map(() => Math.random().toString()),
  );

  const addBuildingSection = () => {
    onChange([...value, { building_type_id: "", structure_type_ids: [] }]);
    setUniqueIds([...uniqueIds, Math.random().toString()]);
  };

  const removeBuildingSection = (buildingTypeId: string) => {
    onChange(
      value.filter((section) => section.building_type_id !== buildingTypeId),
    );
    const dupValue = value.slice();
    uniqueIds.splice(
      uniqueIds.findIndex(
        (id) =>
          dupValue[uniqueIds.indexOf(id)].building_type_id === buildingTypeId,
      ),
      1,
    );
    setUniqueIds(uniqueIds);
  };

  const handleBuildingTypeIdChange = (
    index: number,
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedValue = e.target.value !== "" ? Number(e.target.value) : "";
    onChange(
      value.map((section, i) =>
        i === index
          ? {
              building_type_id: selectedValue.toString(),
              structure_type_ids: [],
            }
          : section,
      ),
    );
  };

  const handleStructureTypesChange = (
    buildingTypeId: string,
    structureTypeIds: string[],
  ) => {
    onChange(
      value.map((section) =>
        section.building_type_id === buildingTypeId
          ? {
              building_type_id: buildingTypeId,
              structure_type_ids: structureTypeIds,
            }
          : section,
      ),
    );
  };

  const getBuildingTypeNameById = (category: string) => {
    const selected = buildingTypes.find(
      (opt) => opt.id.toString() === category,
    );
    return selected ? selected.name : "";
  };

  return (
    <Stack spacing={4}>
      {value.map((section, index) => {
        return (
          <Stack key={uniqueIds[index]} spacing={0}>
            <Flex gap={2}>
              <Box flex={1}>
                <Box>
                  <Select
                    placeholder="選択してください"
                    onChange={(e) => handleBuildingTypeIdChange(index, e)}
                    value={section.building_type_id}
                    isInvalid={errors?.[index]?.building_type_id != null}
                    isDisabled={isDisabled}
                  >
                    {buildingTypes
                      .filter((buildingType) => {
                        const selectedBuildingTypes = value.map(
                          (section) => section.building_type_id,
                        );
                        return (
                          section.building_type_id ===
                            buildingType.id.toString() ||
                          !selectedBuildingTypes.includes(
                            buildingType.id.toString(),
                          )
                        );
                      })
                      .map((option) => (
                        <option value={option.id} key={option.id}>
                          {option.name}
                        </option>
                      ))}
                  </Select>
                  {errors?.[index]?.building_type_id && (
                    <InputError>
                      {errors[index].building_type_id.message}
                    </InputError>
                  )}
                </Box>
                {section.building_type_id !== "" && (
                  <Stack spacing={0} mt={2}>
                    <Box
                      width="0"
                      height="0"
                      borderLeft="6px solid transparent"
                      borderRight="6px solid transparent"
                      borderBottom="8px solid"
                      borderBottomColor="brand.50"
                      borderTop="none"
                      marginLeft={4}
                    />
                    <Stack
                      spacing={2}
                      backgroundColor="brand.50"
                      paddingInline={5}
                      paddingBlock={4}
                      borderRadius="2px"
                    >
                      <FormLabel required>
                        {getBuildingTypeNameById(section.building_type_id)} -
                        {labelPrefix}構造種別
                      </FormLabel>
                      <MultiSelect
                        options={structureTypes.map((opt) => ({
                          value: opt.id.toString(),
                          label: opt.name,
                        }))}
                        value={section.structure_type_ids.map((id) => ({
                          value: id,
                          label:
                            structureTypes.find(
                              (opt) => opt.id.toString() === id,
                            )?.name ?? "",
                        }))}
                        onChange={(selected) => {
                          handleStructureTypesChange(
                            section.building_type_id,
                            selected.map((opt) => opt.value),
                          );
                        }}
                        isInvalid={errors?.[index]?.structure_type_ids != null}
                        isDisabled={isDisabled}
                      />
                      {errors?.[index]?.structure_type_ids && (
                        <InputError>
                          {errors[index].structure_type_ids.message}
                        </InputError>
                      )}
                    </Stack>
                  </Stack>
                )}
              </Box>
              {value.length > 1 && !isDisabled && (
                <Box>
                  <Box
                    as="button"
                    p={2.5}
                    onClick={() =>
                      removeBuildingSection(section.building_type_id)
                    }
                  >
                    <CloseIcon boxSize={5} display="block" />
                  </Box>
                </Box>
              )}
            </Flex>
          </Stack>
        );
      })}
      {buildingTypes.length > value.length && !isDisabled && (
        <Stack align="flex-start">
          <Button
            size="xs"
            variant="outline"
            leftIcon={<AddIcon boxSize={4} />}
            style={{ width: "auto" }}
            onClick={addBuildingSection}
          >
            {labelPrefix}建物種別を追加
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default BuildingTypeStructureTypeFieldForEdit;
